import fp from 'micro-fp';

import mapPinClockInSvg from '../../assets/svg/UI/map-pin-clock-in.svg';
import mapPinClockOutSvg from '../../assets/svg/UI/map-pin-clock-out.svg';
import mapPinLocationGraySvg from '../../assets/svg/UI/map-pin-location-gray.svg';

const slice = fp.curry((from, to, subject) => subject.slice(from, to));
const trim = (str) => str.trim();

const parseCoordinates = fp.compose(fp.map(Number), fp.map(trim), fp.split(','), slice(1, -1));
const parseBounds = fp.match(/\([^)(]+\)/g);

export default function googleMap(GoogleMapsService) {
  'ngInject';

  return {
    restrict: 'E',
    scope: {
      center: '=',
      zoom: '=',
      pin: '=',
      bounds: '=',
      clockInPin: '<?',
      clockOutPin: '<?',
      readonly: '<?',
      pinRadius: '<?',
      hideMapTypeControl: '<?',
      smallControls: '<',
      hideFullScreenControl: '<?',
    },
    link($scope, $element) {
      const maps = GoogleMapsService.maps;
      let pin;
      let map;
      const options = {
        streetViewControl: false,
        scrollwheel: false,
        mapTypeControl: !$scope.hideMapTypeControl,
        mapTypeControlOptions: {
          style: GoogleMapsService.maps?.MapTypeControlStyle?.HORIZONTAL_BAR,
          mapTypeIds: ['roadmap', 'satellite'],
        },
        center: new maps.LatLng(40, 10.5),
        zoom: ($scope.zoom && Number($scope.zoom)) || 1,
        fullscreenControl: !$scope.hideFullScreenControl,
      };

      init();
      function init() {
        if ($scope.smallControls) {
          options.controlSize = 20;
        }

        if ($scope.center) {
          options.center = new maps.LatLng(...parseCoordinates($scope.center));
        }

        map = new maps.Map($element[0], options);
        $scope.map = map;

        if ($scope.pin) {
          _updatePin($scope.pin);
        }
      }

      $scope.$watch('center', (val) => {
        if (val) {
          const coordinates = parseCoordinates(val);
          map.setCenter(new maps.LatLng(...coordinates));
        }
      });

      $scope.$watch('zoom', (val) => {
        if (val) {
          map.setZoom(Number(val));
        }
      });

      $scope.$watch('pin', (val) => {
        if (val) {
          _updatePin(val);
        }
      });

      function _updatePin(val) {
        const coordinates = parseCoordinates(val);
        const position = new maps.LatLng(...coordinates);

        if (!pin) {
          pin = new maps.Marker({
            map,
            position,
            draggable: !$scope.readonly,
            icon: $scope.pinRadius ? mapPinLocationGraySvg : undefined,
          });

          if (!$scope.readonly) {
            maps.event.addListener(pin, 'dragend', (point) => {
              $scope.$evalAsync(() => {
                $scope.pin = point.latLng.toString();
              });
            });
          }

          if ($scope.pinRadius) {
            $scope._pinCirle = new maps.Circle({
              strokeWeight: 0,
              fillColor: '#37556A',
              fillOpacity: 0.175,
              map,
              center: position,
              radius: $scope.pinRadius, // in meters
            });
          }
        } else {
          pin.setPosition(position);
        }
      }

      $scope.$watch('bounds', (val) => {
        if (val) {
          const coordinatesPairs = fp.compose(fp.map(parseCoordinates), parseBounds)(val);

          map.fitBounds(
            new maps.LatLngBounds(
              new maps.LatLng(...coordinatesPairs[0]),
              new maps.LatLng(...coordinatesPairs[1])
            )
          );
        }
      });

      maps.event.addListener(map, 'idle', () => {
        $scope.$evalAsync(() => {
          $scope.zoom = map.getZoom();
          $scope.center = map.getCenter().toString();
        });
      });

      if (!$scope.readonly) {
        maps.event.addListener(map, 'click', (point) => {
          $scope.$evalAsync(() => {
            $scope.pin = point.latLng.toString();
          });
        });
      }

      const bounds = new maps.LatLngBounds();
      if (pin) {
        bounds.extend(pin.getPosition());
      }

      if ($scope.clockInPin) {
        const position = new maps.LatLng($scope.clockInPin[1], $scope.clockInPin[0]);
        const clockInPin = new maps.Marker({ map, position, icon: mapPinClockInSvg });
        bounds.extend(position);
        let clockInPinInfoShown = false;

        maps.event.addListener(clockInPin, 'click', () => {
          if (!clockInPinInfoShown) {
            clockInPin.setLabel({
              text: 'clock in',
              className: 'map-pin-text-info-clock-in',
            });
          } else {
            clockInPin.setLabel(null);
          }

          clockInPinInfoShown = !clockInPinInfoShown;
        });
      }

      if ($scope.clockOutPin) {
        const position = new maps.LatLng($scope.clockOutPin[1], $scope.clockOutPin[0]);
        const clockOutPin = new maps.Marker({ map, position, icon: mapPinClockOutSvg });
        bounds.extend(position);
        let clockOutPinInfoShown = false;

        maps.event.addListener(clockOutPin, 'click', () => {
          if (!clockOutPinInfoShown) {
            clockOutPin.setLabel({
              text: 'clock out',
              className: 'map-pin-text-info-clock-out',
            });
          } else {
            clockOutPin.setLabel(null);
          }

          clockOutPinInfoShown = !clockOutPinInfoShown;
        });
      }

      if ($scope.clockInPin || $scope.clockOutPin) {
        map.setCenter(bounds.getCenter());
        map.fitBounds(bounds);
        map.setZoom(map.getZoom() - 1); // makes sure the markers don't appear right on the edge
      }
    },
  };
}
